import { useAdapter } from '../../application/data/use-adapter';
import type { TableAdapter } from '../../ports';
import { Icon } from '../shared/Icon';

const COUNT_FMT = new Intl.NumberFormat('en-GB');

function formatCount(value: number): string {
    return COUNT_FMT.format(value);
}

export interface PaginationProps<T> {
    adapter: TableAdapter<T>;
}

interface StatusSummaryProps {
    start: number;
    end: number;
    total: number | undefined;
}

function StatusSummary(props: StatusSummaryProps): JSX.Element {
    const { start, end, total } = props;
    return (
        <span className="ics-table__status-summary">
            <strong>{formatCount(start)}</strong> to{' '}
            <strong>{formatCount(end)}</strong>
            {total === undefined ? null : (
                <>
                    {' '}
                    of <strong>{formatCount(total)}</strong>
                </>
            )}
        </span>
    );
}

export function TablePagination<T>(
    props: PaginationProps<T>,
): JSX.Element | null {
    const { adapter } = props;
    const snapshot = useAdapter(adapter);
    const {
        page,
        hasNext,
        hasPrev,
        loading,
        pageCount,
        capabilities,
        rows,
        offset,
    } = snapshot;

    const total = snapshot.totalCount;
    const isFilterPossible =
        capabilities.filter !== false || capabilities.sort !== false;
    const hasRows = rows.length > 0;
    const hasTotal = total !== undefined && total > 0;
    const showHint = isFilterPossible && (hasRows || hasTotal);

    const hasPagingAdapter =
        adapter.next !== undefined ||
        adapter.prev !== undefined ||
        adapter.first !== undefined ||
        adapter.last !== undefined ||
        adapter.goTo !== undefined;

    const hasSummary = hasRows || total !== undefined;

    if (!hasPagingAdapter && !hasSummary && !showHint) {
        return null;
    }

    if (!hasRows && total === 0) {
        return null;
    }

    const showFirst = adapter.first !== undefined;
    const showPrev = adapter.prev !== undefined;
    const showNext = adapter.next !== undefined;
    const showLast =
        adapter.last !== undefined && capabilities.totalCount === true;

    const start = rows.length === 0 ? 0 : offset + 1;
    const end = rows.length === 0 ? 0 : offset + rows.length;

    return (
        <footer className="ics-table__footer">
            {showHint ? (
                <div className="ics-table__status-hint">
                    Use search, filter and sort to narrow down the results
                </div>
            ) : (
                <div />
            )}
            <div className="ics-table__pagination">
                {hasSummary ? (
                    <StatusSummary start={start} end={end} total={total} />
                ) : null}
                {hasPagingAdapter ? (
                    <nav
                        className="ics-table__page-controls"
                        aria-label="Pagination"
                    >
                        {showFirst ? (
                            <button
                                type="button"
                                className="ics-table__page-button"
                                aria-label="First page"
                                onClick={() => adapter.first?.()}
                                disabled={loading || page === 0}
                            >
                                <Icon name="first_page" />
                            </button>
                        ) : null}
                        {showPrev ? (
                            <button
                                type="button"
                                className="ics-table__page-button"
                                aria-label="Previous page"
                                onClick={() => adapter.prev?.()}
                                disabled={loading || !hasPrev}
                            >
                                <Icon name="chevron_left" />
                            </button>
                        ) : null}
                        <span className="ics-table__page-label">
                            Page <strong>{formatCount(page + 1)}</strong>
                            {pageCount === undefined ? null : (
                                <>
                                    {' '}
                                    of <strong>{formatCount(pageCount)}</strong>
                                </>
                            )}
                        </span>
                        {showNext ? (
                            <button
                                type="button"
                                className="ics-table__page-button"
                                aria-label="Next page"
                                onClick={() => adapter.next?.()}
                                disabled={loading || !hasNext}
                            >
                                <Icon name="chevron_right" />
                            </button>
                        ) : null}
                        {showLast ? (
                            <button
                                type="button"
                                className="ics-table__page-button"
                                aria-label="Last page"
                                onClick={() => adapter.last?.()}
                                disabled={loading || !hasNext}
                            >
                                <Icon name="last_page" />
                            </button>
                        ) : null}
                    </nav>
                ) : null}
            </div>
        </footer>
    );
}
